/**
 * <ValidatedForm> — design-system wrapper around useValidatedForm so any
 * page adopts the scroll-to-field / red highlight / toast / focus /
 * blocked-button behaviour with minimal code:
 *
 *   <ValidatedForm
 *     fields={[
 *       { id: 'productId', label: 'Product', required: true, errorMessage: 'Please select a product' },
 *       { id: 'imageUrl', label: 'Hero image', required: true, errorMessage: 'Please upload a hero image' },
 *     ]}
 *     values={{ productId, imageUrl }}
 *     onSubmit={handleSubmit}
 *   >
 *     <ValidatedField id="productId" label="Product"><ProductPicker … /></ValidatedField>
 *     <ValidatedField id="imageUrl" label="Hero image"><ImageUpload … /></ValidatedField>
 *     <SubmitButton label="Publish" />
 *   </ValidatedForm>
 *
 * Pieces (also usable standalone with a `form` from useValidatedForm):
 *   - <ValidatedField id label hint bare>  container that registers as the
 *     scroll/focus target, paints the red state, renders the inline error
 *     (aria-describedby target). `bare` skips the label (for Polaris
 *     TextField/Select which draw their own — pass form.fieldProps(id)
 *     to them: `error: true` = Polaris red state, message stays here).
 *   - <SubmitButton label …>               Polaris Button wired to submit;
 *     never hard-disabled for validation (click re-runs scroll-to-field),
 *     dimmed + tooltip while blocked, `loading` while submitting.
 *   - <FormErrorToast>                     fixed top-center summary,
 *     role="alert" aria-live="assertive", auto-closes when valid.
 */
import { Button, Text } from '@shopify/polaris';
import { createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

import useValidatedForm from './useValidatedForm';

const FormContext = createContext(null);

export function useFormContext() {
  return useContext(FormContext);
}

export default function ValidatedForm({ fields, values, onSubmit, children, className, toast = true, as = 'form', ...rest }) {
  const form = useValidatedForm({ fields, values, onSubmit, ...rest });
  const Tag = as;
  const tagProps = as === 'form' ? { noValidate: true, onSubmit: form.submit } : {};
  return (
    <FormContext.Provider value={form}>
      <Tag className={`au-vform${className ? ` ${className}` : ''}`} {...tagProps}>
        {typeof children === 'function' ? children(form) : children}
      </Tag>
      {toast && <FormErrorToast form={form} />}
    </FormContext.Provider>
  );
}

export function ValidatedField({ id, label, hint, required, bare = false, form: formProp, children, className, style }) {
  const ctx = useFormContext();
  const form = formProp || ctx;
  const error = form?.errors?.[id] || null;
  const errorId = `${id}-error`;
  const invalid = !!error;
  const ref = form?.register ? form.register(id) : undefined;
  return (
    <div
      ref={ref}
      data-field-id={id}
      className={`au-field${invalid ? ' au-field--invalid' : ''}${bare ? ' au-field--bare' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      aria-invalid={invalid ? 'true' : undefined}
      aria-describedby={invalid ? errorId : undefined}
    >
      {!bare && label && (
        <label className="au-field__label" htmlFor={id}>
          {label}{required ? <span className="au-field__req" aria-hidden="true"> *</span> : null}
        </label>
      )}
      {!bare && hint && !invalid && <div className="au-field__hint">{hint}</div>}
      <div className="au-field__control">{children}</div>
      {invalid && (
        <div id={errorId} className="au-field__error" role="alert">
          <span aria-hidden="true" className="au-field__error-icon">!</span>
          {error}
        </div>
      )}
    </div>
  );
}

export function SubmitButton({ label, children, form: formProp, variant = 'primary', size, fullWidth, tone, icon, className, disabled = false, disabledReason, loading, onClick, ...rest }) {
  const ctx = useFormContext();
  const form = formProp || ctx;
  const blocked = !!form?.blocked;
  const busy = loading ?? !!form?.submitting;
  // `disabled` here means "genuinely unavailable" (e.g. a job is already
  // running) — never used for validation gating, which is `blocked`.
  const title = blocked ? form.blockedReason : (disabled && disabledReason) || undefined;
  return (
    <span
      className={`au-submit${blocked ? ' au-submit--blocked' : ''}${fullWidth ? ' au-submit--full' : ''}${className ? ` ${className}` : ''}`}
      title={title}
      data-blocked={blocked ? 'true' : undefined}
    >
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        tone={tone}
        icon={icon}
        submit={false}
        loading={busy}
        disabled={disabled}
        accessibilityLabel={title ? `${label || ''}. ${title}` : undefined}
        onClick={(e) => { onClick?.(e); if (form) form.submit(e); }}
        {...rest}
      >
        {label || children}
      </Button>
    </span>
  );
}

export function FormErrorToast({ form: formProp }) {
  const ctx = useFormContext();
  const form = formProp || ctx;
  const toast = form?.toast;
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="au-form-toast-host" aria-live="assertive" aria-atomic="true">
      {toast && (
        <div className="au-form-toast" role="alert">
          <span aria-hidden="true" className="au-form-toast__icon">!</span>
          <div className="au-form-toast__body">
            <Text as="span" variant="bodyMd" fontWeight="semibold">{toast.message}</Text>
          </div>
          <button type="button" className="au-form-toast__close" aria-label="Dismiss" onClick={form.dismissToast}>×</button>
        </div>
      )}
    </div>,
    document.body,
  );
}

export { useValidatedForm };
